import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import { useNotifications } from '@/context/NotificationsContext';
import { useAsyncData } from '@/hooks/useAsyncData';
import { formatDate } from '@/lib/format';
import { systemSettings } from '@/mocks/settings';
import { fetchEscalations, fetchNewOrders, fetchSummary } from '@/services/dashboardService';
import EscalationSection from './components/EscalationSection';
import NewOrdersSection from './components/NewOrdersSection';
import SummaryCards from './components/SummaryCards';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const summary = useAsyncData(fetchSummary);
  const newOrders = useAsyncData(fetchNewOrders);
  const escalations = useAsyncData(fetchEscalations);
  const { subscribeEvents } = useNotifications();

  const refreshAll = () => {
    summary.refetch();
    newOrders.refetch();
    escalations.refetch();
  };

  // Refresh the related parts whenever a live notification arrives (FB09).
  useEffect(() => subscribeEvents(() => refreshAll()), [subscribeEvents]);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('home.title')}</h1>
          <p className="mt-1 text-sm text-foreground-500">{t('home.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs text-foreground-600">
            <i className="ri-calendar-line text-base text-foreground-400" aria-hidden="true" />
            <span className="hidden sm:inline">{t('home.today')}:</span>
            {formatDate(new Date(), i18n.language)}
          </span>
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={refreshAll}>
            {t('home.refresh')}
          </Button>
        </div>
      </header>

      <SummaryCards
        data={summary.data}
        loading={summary.loading}
        error={summary.error}
        onRetry={summary.refetch}
      />

      <EscalationSection
        alerts={escalations.data}
        loading={escalations.loading}
        error={escalations.error}
        onRetry={escalations.refetch}
      />

      <NewOrdersSection
        orders={newOrders.data}
        loading={newOrders.loading}
        error={newOrders.error}
        onRetry={newOrders.refetch}
        currency={systemSettings.currency}
      />
    </div>
  );
}